import { Check, Send, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Skeleton,
  StatusPill,
  toast,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { withGstPaise } from "../price-math.ts";
import {
  ORDER_STATUS_LABEL,
  ORDER_STATUS_TONE,
  exactMoney,
  orderMoney,
  shortDate,
  type OrderStatus,
} from "./order-format.ts";
import { shortDay } from "../date-window.ts";
import { PrebookPill } from "../prebook-pill.tsx";
import { BookingCard } from "../booking-card.tsx";

/**
 * A proforma is created unnumbered — numbers are allocated at finalize, not at
 * creation — so a freshly converted order links to one with an empty
 * `docNumber`. Naming it "Draft proforma" says what it is; rendering the empty
 * string would look like a bug.
 */
function proformaLabel(docNumber: string | null): string {
  return docNumber?.trim() ? docNumber : "Draft proforma";
}

/**
 * Which moves an operator is offered from here. Mirrors `ORDER_TRANSITIONS` in
 * the domain — the server is the enforcement; this only decides what to draw.
 */
const NEXT_ACTIONS: Record<OrderStatus, ReadonlyArray<{ to: OrderStatus; label: string }>> = {
  draft: [{ to: "confirmed", label: "Confirm" }],
  confirmed: [{ to: "approved", label: "Approve" }],
  approved: [{ to: "fulfilled", label: "Mark fulfilled" }],
  fulfilled: [],
  rejected: [],
};

/**
 * Store admin — Order detail (Commerce, M3).
 * @see prototype/store/admin/all.html#orders-detail
 */
export function OrderDetailPage({ orderId }: { orderId: string }) {
  const utils = trpc.useUtils();
  const q = trpc.storeOrders.get.useQuery({ orderId });
  const canApprove = usePermission("store.orders.approve");
  // The proforma lives in the sales module, behind its own permission — see the
  // Payment card below.
  const canViewProformas = usePermission("invoices.view");

  const transition = trpc.storeOrders.transition.useMutation({
    onSuccess: async (r) => {
      await Promise.all([
        utils.storeOrders.get.invalidate({ orderId }),
        utils.storeOrders.list.invalidate(),
        utils.storeOrders.counts.invalidate(),
      ]);
      toast.success(`Order ${ORDER_STATUS_LABEL[r.status].toLowerCase()}`);
    },
    onError: (e) => toast.error("Couldn't update the order", { description: storeErrorMessage(e) }),
  });

  const o = q.data;

  if (!o) {
    return (
      <StoreAdminLayout
        breadcrumbs={[
          { label: "Store", to: "/store/admin" },
          { label: "Orders", to: "/store/admin/orders" },
          { label: "…" },
        ]}
      >
        <StorePageColumn>
          <BackButton fallbackTo="/store/admin/orders" label="Back to orders" />
          <div className="mt-4">
            {q.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <p className="text-sm text-muted">Order not found.</p>
            )}
          </div>
        </StorePageColumn>
      </StoreAdminLayout>
    );
  }

  // Never label by status: a confirmed order that somehow has no number must
  // not read "Draft order".
  const title = o.docNumber || "Unnumbered order";

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Orders", to: "/store/admin/orders" },
        { label: title },
      ]}
    >
      {/* A lines table beside the 320px rail — `grid`, not the form default. */}
      <StorePageColumn variant="grid">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-5">
          <div className="flex items-start gap-4">
            <BackButton
              fallbackTo="/store/admin/orders"
              label="Back to orders"
              className="mt-1"
            />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-mono text-2xl font-bold tracking-tight text-ink leading-tight">
                  {title}
                </h1>
                <StatusPill tone={ORDER_STATUS_TONE[o.status]} size="sm" dot>
                  {ORDER_STATUS_LABEL[o.status]}
                </StatusPill>
                {/* Identity → state → alarm. The neutral kind marker sits after
                    the status so the coloured pill still reads first. */}
                <PrebookPill kind={o.kind} />
                {o.pastDispatch ? (
                  <StatusPill tone="bad" size="sm" dot>
                    Past dispatch
                  </StatusPill>
                ) : null}
              </div>
              <p className="mt-1 text-sm text-muted">
                {/* Placed is a timestamp (viewer's zone); Dispatch by is a calendar
                    day and stays UTC-pinned. Reads `effectiveDispatchBy` so a
                    pre-book order shows the end of its window (spec 16 R5). */}
                {o.customerName} · Placed {shortDate(o.createdAt)} · Dispatch by{" "}
                {shortDay(o.effectiveDispatchBy)}
              </p>
            </div>
          </div>
          {canApprove ? (
            <div className="flex items-center gap-2">
              {NEXT_ACTIONS[o.status].map((a) => (
                <Button
                  key={a.to}
                  intent="primary"
                  size="md"
                  disabled={transition.isPending}
                  onClick={() => transition.mutate({ orderId, status: a.to })}
                >
                  {a.to === "fulfilled" ? (
                    <Check size={14} aria-hidden />
                  ) : (
                    <Send size={14} aria-hidden />
                  )}
                  {a.label}
                </Button>
              ))}
              {o.status !== "fulfilled" && o.status !== "rejected" ? (
                <Button
                  intent="secondary"
                  size="md"
                  disabled={transition.isPending}
                  onClick={() => transition.mutate({ orderId, status: "rejected" })}
                >
                  <X size={14} aria-hidden />
                  Reject
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold text-ink">Lines</h2>
            </CardHeader>
            <CardBody>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Product</th>
                      <th className="px-3 py-2 text-left font-medium">SKU</th>
                      <th className="px-3 py-2 text-right font-medium">Qty</th>
                      <th className="px-3 py-2 text-right font-medium">Price / pack</th>
                      <th className="px-3 py-2 text-right font-medium">
                        <span className="whitespace-nowrap">Line value</span>{" "}
                        <span className="whitespace-nowrap">(incl. GST)</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.lines.map((l) => (
                      <tr key={l.id} className="border-b border-line">
                        <td className="px-3 py-2 text-ink">
                          {l.name}
                          {/* The pack this line was ordered against (spec 13, R7). */}
                          {l.packName ? (
                            <div className="text-xs text-muted">{l.packName}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-muted">{l.sku ?? "—"}</td>
                        {/* Pack-denominated: qty is packs, pieces = qty × pack.pieces. */}
                        <td className="px-3 py-2 text-right text-ink">
                          {l.packPieces !== null ? (
                            <>
                              <span className="font-mono tabular-nums">{l.qty}</span>{" "}
                              <span className="text-xs text-muted">packs</span>
                              <div className="text-xs text-muted tabular-nums">
                                {l.qty * l.packPieces} pcs
                              </div>
                            </>
                          ) : (
                            <span className="font-mono tabular-nums">{l.qty}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-2">
                          {exactMoney(l.capturedPricePaise)}
                        </td>
                        {/* Line value incl. GST — the money answer (spec 13, AC11). */}
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                          {exactMoney(withGstPaise(BigInt(l.lineTotalPaise), l.taxBps).toString())}
                        </td>
                      </tr>
                    ))}
                    {/* Totals live inside the lines table, as the deck draws it. */}
                    <tr className="font-semibold">
                      <td className="px-3 py-2 text-right text-ink" colSpan={4}>
                        Subtotal
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                        {exactMoney(o.subtotalPaise)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2 text-right text-muted" colSpan={4}>
                        GST
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-2">
                        {exactMoney(o.taxPaise)}
                      </td>
                    </tr>
                    <tr className="text-base font-semibold">
                      <td className="px-3 py-2 text-right text-ink" colSpan={4}>
                        Total
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                        {exactMoney(o.totalPaise)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          <div className="grid gap-5 lg:content-start">
            {/* First in the rail — it is what makes this order unusual, and it
                is what the reader is scanning for. Renders nothing at all for an
                at-once order, so that rail is unchanged (AC5). */}
            <BookingCard
              kind={o.kind}
              deliveryStartsOn={o.deliveryStartsOn}
              deliveryEndsOn={o.deliveryEndsOn}
              sourceCollectionId={o.sourceCollectionId}
              sourceCollectionName={o.sourceCollectionName}
              sourceCollectionStatus={o.sourceCollectionStatus}
              overdue={o.pastDispatch}
            />
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Buyer</h2>
              </CardHeader>
              <CardBody>
                <div className="font-medium text-ink">{o.customerName}</div>
                {o.customerGstin ? (
                  <div className="mt-0.5 font-mono text-xs text-muted">{o.customerGstin}</div>
                ) : null}
                {o.shipToText ? (
                  <div className="mt-2 text-xs text-muted">Ship to: {o.shipToText}</div>
                ) : null}
                {o.buyerNotes ? (
                  <p className="mt-3 border-t border-line pt-3 text-xs text-ink-2">
                    {o.buyerNotes}
                  </p>
                ) : null}
              </CardBody>
            </Card>

            {/* The deck draws Terms / Due / Received / Outstanding here. Every
                one of those comes off the invoice chain, which needs the
                approve→proforma hand-off — so this is an honest empty state
                rather than four fabricated figures. */}
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Payment</h2>
              </CardHeader>
              <CardBody>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Order value</span>
                  <span className="font-mono tabular-nums text-ink">
                    {orderMoney(o.totalPaise)}
                  </span>
                </div>
                {o.proformaId ? (
                  <div className="mt-3 flex items-center justify-between border-t border-line pt-3 text-sm">
                    <span className="text-muted">Proforma</span>
                    {/* The proforma screens are gated on `invoices.view`, which a
                        store operator may not hold. Everyone learns the order is
                        now billable; only those who can open the document get a
                        link, so nobody is handed a dead one. */}
                    {canViewProformas ? (
                      <Link
                        to="/sales/proformas/$proformaId"
                        params={{ proformaId: o.proformaId }}
                        className="font-mono text-sm tabular-nums text-ink hover:text-sky-700"
                      >
                        {proformaLabel(o.proformaDocNumber)}
                      </Link>
                    ) : (
                      <span className="font-mono tabular-nums text-ink">
                        {proformaLabel(o.proformaDocNumber)}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
                    Terms, due date and outstanding arrive once an approved order raises a
                    proforma.
                  </p>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </StorePageColumn>
    </StoreAdminLayout>
  );
}
